/**
 * The scripted camera tour's state machine.
 *
 * The tour flies the free camera through a fixed sequence of phases for every
 * body in its itinerary — transit in, orbit, spiral descent, a low surface
 * pass, and a climb back out — and then moves to the next stop. It is a pure
 * camera exercise: it never touches the vessel, the physics or the simulation
 * clock, and stopping it leaves the free camera exactly where it was.
 *
 * The rest of the tour already lived in this folder — pose maths in
 * `TourPath`, itinerary building in `TourPlan`, timings in `TourConfig` and
 * `tour.config.json` — but the machine that drives them was six hundred lines
 * inside `Game`, which is why `Game` grew to know about spline sample counts
 * and camera pan cycles. It is here now, and `Game` reaches it through the
 * narrow `TourHost` below.
 *
 * `TourHost` is deliberately small and worth reading as the whole coupling
 * surface: the director needs the system's frames, the clock, the free
 * camera's pose, the streamed surfaces for its terrain scans, and the ability
 * to say which body the camera currently belongs to. That last part is why
 * `freeFlightBody` and `freeFlightFrame` are writable — every transit reassigns
 * the camera to a new body, and the surface pass runs in that body's rotating
 * frame while every other phase runs in its inertial one. The director sets
 * them directly rather than through `Game.setFreeFlightFrame`, because it has
 * already computed the pose in the destination frame itself and does not want
 * the conversion that method exists to perform.
 */
import { Quat } from '@/core/math/Quat';
import { Vec3 } from '@/core/math/Vec3';
import { clamp01, smoothstep, smootherstep, TAU } from '@/core/math/scalar';
import { latLonFromPosition } from '@/frames/PlanetaryCoordinates';
import { PlanetSurface } from '@/planet/PlanetSurface';
import { CelestialBody } from '@/universe/CelestialBody';
import { SolarSystem } from '@/universe/SolarSystem';
import { TourConfig, resolveTourConfig, tourFlybySweepRad } from './TourConfig';
import { TourPhase, TourStop, buildTourStops } from './TourPlan';
import {
  OrbitBasis,
  orbitBasis,
  orbitPose,
  seedAngleFromId,
  spiralPose,
  splinePosition,
  surfaceFlybyPose,
} from './TourPath';

/** Control points laid along a tour stop's orbit+descend spline, split across the two proportional to their configured duration. */
const TOUR_SPACE_SPLINE_SAMPLES = 24;
/** How long, at each end of the surface pass, its forward-facing orientation blends with the orbit/descend segment's look-at-centre orientation. */
const TOUR_ORIENTATION_BLEND_SEC = 2.5;
/**
 * How sharply transit's approach decelerates. Higher pulls more of the
 * closure into the tail: at t=0.5 a value of 5 has already covered ~92% of
 * the distance but is still visibly slowing, rather than 50%/50%.
 */
const TOUR_TRANSIT_APPROACH_SHARPNESS = 5;
/** Clearance kept above the highest terrain sampled along a planned surface pass, metres. */
const TOUR_FLYBY_TERRAIN_CLEARANCE_M = 250;
/**
 * Ground spacing between elevation samples along a planned surface pass,
 * metres, and the hard cap on how many of them are taken.
 *
 * The scan used to be a flat 14 samples over the whole pass regardless of its
 * length — roughly one sample every 100 km on Halcyon, which cannot see a
 * mountain range at all, let alone clear it. Spacing the samples by ground
 * distance instead ties the scan's resolution to the terrain it is scanning.
 * `elevationInDirection` is memoised per direction cell, so a denser scan is
 * cheap and runs once per stop, not per frame.
 */
const TOUR_FLYBY_TERRAIN_SAMPLE_SPACING_M = 400;
const TOUR_FLYBY_TERRAIN_MAX_SAMPLES = 320;
/**
 * Extra ground scanned past each end of a planned surface pass, metres.
 *
 * The pass's start direction is recomputed in the body-fixed frame at the
 * moment the descent hands over, and the planning here happens the better part
 * of a minute of simulated time earlier, so the two disagree by however far
 * the body turned in between. Planning at the *predicted* handover time
 * (see `beginTransitTo`) removes almost all of that; this pad covers the
 * frame-quantisation remainder.
 */
const TOUR_FLYBY_TERRAIN_SCAN_MARGIN_M = 2000;
/**
 * How strongly a candidate orbit seed is scored on landing over land rather
 * than open water, relative to the daylight term it shares the search with.
 * Both terms are normalised to [0,1] first, so this is a straight ratio: a
 * pass over land is worth this many radians of longitude error from noon.
 */
const TOUR_FLYBY_LAND_WEIGHT = 1.4;
/** Elevation samples used to score a candidate seed's land coverage; coarse on purpose, it only has to tell a continent from an ocean. */
const TOUR_FLYBY_LAND_SAMPLES = 12;
/** Left-right camera drift during the surface pass, for a scanning rather than rigidly-forward look. */
const TOUR_FLYBY_PAN_DEG = 14;
const TOUR_FLYBY_PAN_CYCLES = 2;
/** How far off local noon a surface pass is allowed to land, degrees of longitude — keeps it well inside sunrise/sunset. */
const TOUR_FLYBY_DAYLIGHT_HALF_ANGLE_DEG = 70;

/** Live tour progress, for the HUD. */
export interface TourStatus {
  bodyId: string;
  bodyName: string;
  phase: TourPhase;
  phaseProgress: number;
  stopIndex: number;
  stopCount: number;
}

/** The free camera's pose, as the director reads and writes it. */
export interface TourCamera {
  readonly active: boolean;
  readonly position: Vec3;
  readonly orientation: Quat;
}

/** Everything the director needs from the game. See this file's header. */
export interface TourHost {
  readonly system: SolarSystem;
  /** Simulation time, seconds. */
  readonly time: number;
  readonly freeFlight: TourCamera;
  /** Streamed surfaces, for the terrain scans that keep a pass above the ridges. */
  readonly surfaces: ReadonlyMap<string, PlanetSurface>;
  /** The body whose inertial frame holds the free camera's position. Reassigned at every transit. */
  freeFlightBody: CelestialBody | null;
  /** Which of that body's frames the camera's pose is expressed in. */
  freeFlightFrame: 'inertial' | 'fixed';
  /** Fallback departure body for the very first stop. */
  readonly shipReferenceBody: CelestialBody;
  /** Camera position in the reference body's inertial frame. */
  cameraPositionInFrame(): Vec3;
  /** Unit vector toward the star, in a body's inertial frame. */
  sunDirectionAt(body: CelestialBody, positionInBodyFrame: Vec3, t: number): Vec3;
  /** Promotes a body to full terrain rendering. */
  setActiveBody(body: CelestialBody): void;
  /** Turns the free camera on if it is not already. */
  enableFreeFlight(): void;
  notify(severity: 'info' | 'success' | 'warning', title: string, message: string): void;
}

export class TourDirector {
  private config: TourConfig | null = null;
  private stops: TourStop[] = [];
  private stopIndex = -1;
  private phase: TourPhase = 'transit';
  /** Elapsed time in the current phase, for snapshot/UI progress only. */
  private phaseElapsed = 0;
  private basis: OrbitBasis | null = null;
  // Transit is its own eased leg — merging it into the orbit/descend spline
  // was tried and reverted, see `beginTransitTo`'s doc comment for why.
  private readonly transitFrom = new Vec3();
  private readonly transitTo = new Vec3();
  /** Orientation captured at the instant transit begins, so it can ease into facing the destination instead of snapping to it. */
  private transitStartOrientation: Quat = Quat.identity();
  // Orbit + descend are one continuous spline (see `buildOrbitDescendPoints`)
  // so there is no hard direction change where the old code stitched an
  // independent circle and spiral formula together. `spaceElapsed` runs across
  // both phases without resetting; `phaseElapsed` above still resets at their
  // boundary purely to report per-phase progress.
  private spacePoints: Vec3[] = [];
  private spaceElapsed = 0;
  private spaceDuration = 1;
  private readonly flybyStartDir = new Vec3();
  private readonly flybyAxis = new Vec3();
  /** Terrain-safe altitude for the current stop's surface pass, metres — computed once at the descend-to-surface handoff. */
  private flybyAltitude = 0;
  /** Ascend's own orbit basis, built so its `u` axis points exactly at the surface pass's ending position. */
  private ascendBasis: OrbitBasis | null = null;
  private ascendRadiusStart = 0;

  constructor(private readonly host: TourHost) {}

  get active(): boolean {
    return this.stopIndex >= 0;
  }

  /** Starts a tour. Config is optional; unset fields fall back to `tour.config.json`. */
  start(overrides?: Partial<TourConfig>): boolean {
    if (this.active) {
      this.host.notify('info', 'Tour already running', 'Stop the current tour before starting another.');
      return false;
    }
    const config = resolveTourConfig(overrides);
    const stops = buildTourStops(this.host.system, config);
    if (stops.length === 0) {
      this.host.notify('warning', 'Tour', 'No bodies available to tour.');
      return false;
    }

    this.host.enableFreeFlight();
    this.config = config;
    this.stops = stops;
    this.stopIndex = 0;
    const first = this.host.system.find(stops[0].bodyId);
    this.host.notify(
      'info',
      'Tour started',
      `Touring ${stops.length} bodies, starting with ${first?.name ?? stops[0].bodyId}.`,
    );
    this.beginTransitTo(stops[0]);
    return true;
  }

  /** Ends the tour and leaves the free camera exactly where it was. */
  stop(): void {
    if (!this.active) return;
    this.stopIndex = -1;
    this.stops = [];
    this.config = null;
    this.host.notify('info', 'Tour ended', 'Camera under manual control — F8 to return to the vessel.');
  }

  /**
   * Jumps to the next or previous stop, interrupting whatever phase is
   * currently running. Routes through `beginTransitTo` exactly like the
   * normal end-of-stop advance does, so the new leg's spline is built fresh
   * from wherever the camera actually is the moment the button is pressed —
   * not from a precomputed path or the tour's original starting point.
   */
  skipToStop(direction: 1 | -1): boolean {
    if (!this.active) return false;
    const next = this.stopIndex + direction;
    if (next < 0 || next >= this.stops.length) {
      this.host.notify('info', 'Tour', direction > 0 ? 'Already at the last stop.' : 'Already at the first stop.');
      return false;
    }
    this.stopIndex = next;
    const stop = this.stops[next];
    const body = this.host.system.find(stop.bodyId);
    this.host.notify('info', 'Touring', `Skipped to ${body?.name ?? stop.bodyId}.`);
    this.beginTransitTo(stop);
    return true;
  }

  private phaseDuration(phase: TourPhase, cfg: TourConfig): number {
    const durations: Record<TourPhase, number> = {
      transit: cfg.transitDurationSec,
      orbit: cfg.orbitDurationSec,
      descend: cfg.descentDurationSec,
      surface: cfg.surfaceFlybyDurationSec,
      ascend: cfg.descentDurationSec,
    };
    return durations[phase];
  }

  /** Live progress for the UI, or null when no tour is running. */
  status(): TourStatus | null {
    if (!this.active || !this.config) return null;
    const stop = this.stops[this.stopIndex];
    const body = this.host.system.find(stop.bodyId);
    const duration = this.phaseDuration(this.phase, this.config);
    return {
      bodyId: stop.bodyId,
      bodyName: body?.name ?? stop.bodyId,
      phase: this.phase,
      phaseProgress: duration > 0 ? clamp01(this.phaseElapsed / duration) : 1,
      stopIndex: this.stopIndex,
      stopCount: this.stops.length,
    };
  }

  /**
   * Orientation that looks at the body centre from `position` (also in
   * `body`'s inertial frame), falling back to a constant "face the orbit's
   * start direction" pose right next to the centre.
   *
   * Dividing a near-zero position by its own length is hypersensitive to
   * exactly where it sits — normal right on the far side of an interplanetary
   * transit, but a real problem for the *start* of that transit, which is
   * wherever the camera happened to be when the tour began: on the ground,
   * that is only a few thousand kilometres from the centre while the transit
   * itself spans astronomical units, and small wobbles in a near-centre
   * departure point used to produce a 170-degree orientation swing in the
   * first couple of transit frames. Blended by *distance*, not time, so it
   * only ever engages where the singularity actually is.
   */
  private lookAtCentre(position: Vec3, basis: OrbitBasis, orbitRadius: number): Quat {
    const distFromCentre = position.length();
    const stableForward = Quat.lookRotation(basis.u, basis.normal);
    if (distFromCentre < 1) return stableForward;
    const lookCentre = Quat.lookRotation(position.clone().negate(), basis.normal);
    const w = smootherstep(0, orbitRadius * 0.2, distFromCentre);
    return w >= 1 ? lookCentre : Quat.slerp(stableForward, lookCentre, w);
  }

  /**
   * Lays down the control points for a stop's orbit+descend spline, in
   * `body`'s inertial frame: around the orbit circle, then a spiral down to
   * the surface-pass altitude (omitted when the body has no terrain). Sample
   * counts are split across the two legs proportional to their configured
   * duration, so `splinePosition`'s uniform parametrisation —
   * `t = elapsed / totalDuration` — lands each leg's share of samples in the
   * matching share of real time.
   *
   * Transit deliberately is *not* part of this spline (see the note in
   * `beginTransitTo`), which keeps every point here within the same order of
   * magnitude — orbit and descend never differ by more than
   * `orbitStandoffRadii`, unlike a transit leg that can span light-minutes.
   */
  private buildOrbitDescendPoints(
    body: CelestialBody,
    cfg: TourConfig,
    hasSurface: boolean,
    basis: OrbitBasis,
    flybyAltitude: number,
  ): Vec3[] {
    const orbitRadius = body.radius * cfg.orbitStandoffRadii;
    const orbitSweep = TAU * cfg.orbitRevolutions;
    const totalDuration = Math.max(0.1, cfg.orbitDurationSec + (hasSurface ? cfg.descentDurationSec : 0));
    const orbitSamples = Math.max(2, Math.round((TOUR_SPACE_SPLINE_SAMPLES * cfg.orbitDurationSec) / totalDuration));
    const descendSamples = hasSurface ? Math.max(2, TOUR_SPACE_SPLINE_SAMPLES - orbitSamples) : 0;

    const points: Vec3[] = [orbitPose(orbitRadius, 0, basis).position];
    for (let i = 1; i <= orbitSamples; i++) {
      points.push(orbitPose(orbitRadius, (i / orbitSamples) * orbitSweep, basis).position);
    }
    if (hasSurface) {
      // The terrain-cleared altitude, not the configured one. The descent used
      // to spiral down to a flat `surfaceFlybyAltitudeM` above the *datum* and
      // only then, at the handover, jump up to whatever clearance the terrain
      // actually needed — so the last seconds of every descent flew through
      // any range higher than that flat figure, which is most of them.
      const surfaceRadius = body.radius + flybyAltitude;
      const descendAngleSpan = Math.PI * 0.6;
      for (let i = 1; i <= descendSamples; i++) {
        const u = i / descendSamples;
        points.push(spiralPose(orbitRadius, surfaceRadius, orbitSweep, descendAngleSpan, u, u, basis).position);
      }
    }
    return points;
  }

  /**
   * Body-fixed direction and sweep axis for a surface pass starting at
   * `endPositionInertial` — shared by the actual descend-to-surface handoff
   * and the anticipatory orientation blend that runs during the last
   * stretch of the descent, so both derive the pass's starting geometry the
   * same way.
   */
  private flybyStartFrom(
    endPositionInertial: Vec3,
    body: CelestialBody,
    atTime = this.host.time,
  ): { dir: Vec3; axis: Vec3 } {
    const fixedPos = this.host.system.frames.transformPosition(
      endPositionInertial,
      body.inertialFrameId,
      body.fixedFrameId,
      atTime,
    );
    const dir = fixedPos.normalized();
    let axis = Vec3.crossVectors(new Vec3(0, 1, 0), dir);
    if (axis.lengthSq() < 1e-9) axis = Vec3.anyPerpendicular(dir);
    return { dir, axis: axis.normalize() };
  }

  /**
   * An orbit basis whose `u` axis points exactly at `endPositionInertial` —
   * used to climb out of the surface pass without a position jump: the
   * ascend spiral's `angle=0` point is `radius * u`, so starting the spiral
   * there reproduces the surface pass's exact ending position. `referenceUp`
   * (the stop's orbit-plane normal) only shapes which way the climb rolls;
   * any perpendicular would be geometrically valid.
   */
  private ascendBasisFrom(endPositionInertial: Vec3, referenceUp: Vec3): OrbitBasis {
    const u = endPositionInertial.normalized();
    let normal = referenceUp.clone().addScaled(u, -referenceUp.dot(u));
    if (normal.lengthSq() < 1e-9) normal = Vec3.anyPerpendicular(u);
    normal.normalize();
    const v = Vec3.crossVectors(normal, u).normalize();
    return { normal, u, v };
  }

  /**
   * Picks the orbit's starting longitude so the surface pass lands in
   * daylight, and over land rather than open ocean. The natural, hash-seeded
   * angle is kept whenever it already satisfies both (so different stops still
   * land at different times of day and in different places); otherwise this
   * tries a spread of candidate seeds and scores each on how close its
   * descent's planned end point lands to the sub-solar longitude and how much
   * of the pass that follows crosses dry ground. The search reuses exactly the
   * formula `buildOrbitDescendPoints` uses for the descent's final point, so
   * the seed it picks is geometrically consistent with where the camera will
   * actually end up — it is choosing an input to that same shape, not
   * correcting the result afterwards.
   */
  private chooseOrbitSeedAngle(body: CelestialBody, cfg: TourConfig): number {
    const naturalSeed = seedAngleFromId(body.id);
    const inclinationRad = (cfg.orbitInclinationDeg * Math.PI) / 180;
    const finalAngle = TAU * cfg.orbitRevolutions + Math.PI * 0.6;
    const radius = body.radius + cfg.surfaceFlybyAltitudeM;
    const frames = this.host.system.frames;
    const t = this.host.time;

    const sunInertial = this.host.sunDirectionAt(body, new Vec3(), t);
    const sunFixed = frames.transformDirection(sunInertial, body.inertialFrameId, body.fixedFrameId, t);
    const subsolarLon = latLonFromPosition(sunFixed).longitude;

    const endPointFor = (seed: number): { longitude: number; land: number } => {
      const candidateBasis = orbitBasis(body.spinAxis, inclinationRad, seed);
      const endInertial = orbitPose(radius, finalAngle, candidateBasis).position;
      const endFixed = frames.transformPosition(endInertial, body.inertialFrameId, body.fixedFrameId, t);
      const { dir, axis } = this.flybyStartFrom(endInertial, body);
      return {
        longitude: latLonFromPosition(endFixed).longitude,
        land: this.flybyLandFraction(body, cfg, dir, axis),
      };
    };
    const angleDiff = (lon: number): number => {
      const diff = ((((lon - subsolarLon + Math.PI) % TAU) + TAU) % TAU) - Math.PI;
      return Math.abs(diff);
    };

    const halfAngle = (TOUR_FLYBY_DAYLIGHT_HALF_ANGLE_DEG * Math.PI) / 180;
    const natural = endPointFor(naturalSeed);
    // Keep the hash-derived seed when it is already good on both counts, so
    // stops stay varied rather than all converging on the same optimum.
    if (angleDiff(natural.longitude) <= halfAngle && natural.land >= 0.75) return naturalSeed;

    const candidates = 16;
    let best = naturalSeed;
    let bestScore = -Infinity;
    for (let i = 0; i < candidates; i++) {
      const seed = (i / candidates) * TAU;
      const { longitude, land } = endPointFor(seed);
      // Both terms in [0,1], higher is better: 1 at the sub-solar longitude
      // falling to 0 at the anti-solar one, and the pass's dry-land fraction.
      const daylight = 1 - angleDiff(longitude) / Math.PI;
      const score = daylight + TOUR_FLYBY_LAND_WEIGHT * land;
      if (score > bestScore) {
        bestScore = score;
        best = seed;
      }
    }
    return best;
  }

  /**
   * Raises the surface pass above the highest terrain it is actually
   * planned to fly over, so a mountain range under the flight path does not
   * clip through the camera. `elevationInDirection` is the same pure,
   * synchronous procedural function the mesh workers use — no dependency on
   * how much of the chunk mesh has streamed in by now.
   */
  private computeFlybyAltitude(body: CelestialBody, cfg: TourConfig, dir: Vec3, axis: Vec3): number {
    const surface = this.host.surfaces.get(body.id);
    if (!surface) return cfg.surfaceFlybyAltitudeM;
    const sweep = tourFlybySweepRad(cfg, body.radius + cfg.surfaceFlybyAltitudeM);
    const p0 = dir.normalized();
    const p1 = Vec3.crossVectors(axis, p0).normalize();
    const groundTrack = sweep * body.radius + 2 * TOUR_FLYBY_TERRAIN_SCAN_MARGIN_M;
    const samples = Math.min(
      TOUR_FLYBY_TERRAIN_MAX_SAMPLES,
      Math.max(16, Math.ceil(groundTrack / TOUR_FLYBY_TERRAIN_SAMPLE_SPACING_M)),
    );
    let maxElevation = -Infinity;
    // Scans past both ends of the planned track — see
    // TOUR_FLYBY_TERRAIN_SCAN_MARGIN_M. Expressed in radians of the same
    // sweep so a short pass over a small moon is padded by the same metres of
    // ground as a long one over a planet.
    const margin = TOUR_FLYBY_TERRAIN_SCAN_MARGIN_M / Math.max(1, body.radius);
    for (let i = 0; i <= samples; i++) {
      const phi = -margin + (i / samples) * (sweep + 2 * margin);
      const sampleDir = p0.clone().mulScalar(Math.cos(phi)).addScaled(p1, Math.sin(phi)).normalize();
      const elevation = surface.heightField.elevationInDirection(sampleDir.x, sampleDir.y, sampleDir.z);
      if (elevation > maxElevation) maxElevation = elevation;
    }
    return Math.max(cfg.surfaceFlybyAltitudeM, maxElevation + TOUR_FLYBY_TERRAIN_CLEARANCE_M);
  }

  /**
   * Fraction of a planned surface pass that crosses dry land, 0..1. Always 1
   * on a body with no ocean, so a dry world's seed search is decided by
   * daylight alone exactly as it was before this existed.
   */
  private flybyLandFraction(body: CelestialBody, cfg: TourConfig, dir: Vec3, axis: Vec3): number {
    const surface = this.host.surfaces.get(body.id);
    if (!surface || !surface.hasOcean) return 1;
    const sweep = tourFlybySweepRad(cfg, body.radius + cfg.surfaceFlybyAltitudeM);
    const p0 = dir.normalized();
    const p1 = Vec3.crossVectors(axis, p0).normalize();
    const seaLevel = surface.params.seaLevel;
    let land = 0;
    for (let i = 0; i <= TOUR_FLYBY_LAND_SAMPLES; i++) {
      const phi = sweep * (i / TOUR_FLYBY_LAND_SAMPLES);
      const sampleDir = p0.clone().mulScalar(Math.cos(phi)).addScaled(p1, Math.sin(phi)).normalize();
      if (surface.heightField.elevationInDirection(sampleDir.x, sampleDir.y, sampleDir.z) > seaLevel) land++;
    }
    return land / (TOUR_FLYBY_LAND_SAMPLES + 1);
  }

  /**
   * Sets up the flight into `stop`: an orbit basis for the whole stop (its
   * starting longitude chosen so the surface pass lands in daylight), the
   * transit leg into it, and the orbit+descend spline that follows.
   *
   * Transit and orbit+descend are deliberately two separate paths rather
   * than one spline spanning all three, which is what the first version of
   * this did. Transit can cross interplanetary distances — light-minutes —
   * while the orbit it flies into is a few planetary radii across; feeding
   * both scales to one uniformly-parametrised Catmull-Rom spline made the
   * segment joining them wildly unevenly spaced, and the spline overshot by
   * more than an orbit radius right at the join before settling — visible
   * as the camera lurching past the arrival point and snapping back. Two
   * paths at their own natural scales, joined at a shared point, avoids the
   * mismatch outright. It also lets transit properly *decelerate* into
   * arrival (a smoothstep ease with zero velocity at the join) rather than
   * carrying transit speed into the orbit.
   *
   * Departure is wherever the camera actually is, in whichever body it
   * currently belongs to — the vessel's body for the very first stop, the
   * body just left for every one after.
   */
  private beginTransitTo(stop: TourStop): void {
    const host = this.host;
    const frames = host.system.frames;
    const body = host.system.find(stop.bodyId);
    if (!body) {
      this.advanceStop();
      return;
    }
    const cfg = this.config!;
    const fromBody = host.freeFlightBody ?? host.shipReferenceBody;
    const departureLocal = frames.transformPosition(
      host.cameraPositionInFrame(),
      fromBody.inertialFrameId,
      body.inertialFrameId,
      host.time,
    );
    // Captured before `freeFlightFrame`/`freeFlightBody` are reassigned below,
    // so this is genuinely whatever the camera was facing an instant ago —
    // every inertial frame shares the same (identity) orientation relative to
    // root, so only a fixed-frame source (mid surface-pass) needs converting.
    this.transitStartOrientation =
      host.freeFlightFrame === 'fixed' && host.freeFlightBody
        ? frames.transformOrientation(
            host.freeFlight.orientation,
            host.freeFlightBody.fixedFrameId,
            host.freeFlightBody.inertialFrameId,
            host.time,
          )
        : host.freeFlight.orientation.clone();

    const seedAngle = stop.hasSurface ? this.chooseOrbitSeedAngle(body, cfg) : seedAngleFromId(body.id);
    const basis = orbitBasis(body.spinAxis, (cfg.orbitInclinationDeg * Math.PI) / 180, seedAngle);
    this.basis = basis;

    const orbitRadius = body.radius * cfg.orbitStandoffRadii;
    this.transitFrom.copy(departureLocal);
    this.transitTo.copy(orbitPose(orbitRadius, 0, basis).position);

    this.spaceDuration = Math.max(0.1, cfg.orbitDurationSec + (stop.hasSurface ? cfg.descentDurationSec : 0));

    // Plan the surface pass's clearance now, before the descent that has to
    // arrive at it exists: the spline's final point IS the pass's first point,
    // so the two have to be built from one altitude or the handover is a jump
    // — and a descent built from the configured altitude alone is a descent
    // into whatever mountain happens to be there.
    //
    // The ground track is fixed to the body, so the scan is taken at the time
    // the pass will actually start rather than now. Halcyon turns ~0.18 deg in
    // the forty seconds this leg takes, which is several times the length of
    // the pass itself — scanning at the current time surveyed ground the
    // camera never flies over.
    this.flybyAltitude = cfg.surfaceFlybyAltitudeM;
    if (stop.hasSurface) {
      const arrivalTime = host.time + cfg.transitDurationSec + this.spaceDuration;
      const plannedEnd = orbitPose(
        body.radius + cfg.surfaceFlybyAltitudeM,
        TAU * cfg.orbitRevolutions + Math.PI * 0.6,
        basis,
      ).position;
      const planned = this.flybyStartFrom(plannedEnd, body, arrivalTime);
      this.flybyAltitude = this.computeFlybyAltitude(body, cfg, planned.dir, planned.axis);
    }

    this.spacePoints = this.buildOrbitDescendPoints(body, cfg, stop.hasSurface, basis, this.flybyAltitude);
    this.spaceElapsed = 0;

    host.freeFlightBody = body;
    host.freeFlightFrame = 'inertial';
    if (body.def.terrain) host.setActiveBody(body);

    this.phase = 'transit';
    this.phaseElapsed = 0;
  }

  /** Moves to the next stop, or ends the tour once the itinerary is exhausted. */
  private advanceStop(): void {
    const next = this.stopIndex + 1;
    if (next >= this.stops.length) {
      this.host.notify('success', 'Tour complete', `Visited ${this.stops.length} bodies.`);
      this.stopIndex = -1;
      this.stops = [];
      this.config = null;
      return;
    }
    this.stopIndex = next;
    const stop = this.stops[next];
    const body = this.host.system.find(stop.bodyId);
    if (body) this.host.notify('info', 'Touring', `En route to ${body.name}.`);
    this.beginTransitTo(stop);
  }

  /** Advances the current phase and writes the resulting pose into the free camera. */
  update(dt: number): void {
    if (!this.active || !this.config) return;
    const host = this.host;
    const frames = host.system.frames;
    const cfg = this.config;
    const stop = this.stops[this.stopIndex];
    const body = host.system.find(stop.bodyId);
    if (!body) {
      this.advanceStop();
      return;
    }
    const basis = this.basis!;
    const orbitRadius = body.radius * cfg.orbitStandoffRadii;

    this.phaseElapsed += dt;

    if (this.phase === 'transit') {
      const duration = Math.max(0.1, cfg.transitDurationSec);
      const t = clamp01(this.phaseElapsed / duration);
      // Linear/smoothstep easing over raw distance looks instant at
      // interplanetary scale: a planet's apparent size on screen only grows
      // sharply in the last sliver of a percent of the distance covered, so
      // even a gentle time-based ease still dumps almost all of that visual
      // approach into a near-instant blip right at the end. Easing the
      // *closure ratio* exponentially instead means equal time slices cover
      // equal fractions of the remaining distance, which is what an
      // actually-visible slow approach requires at this scale — the same
      // reasoning behind FreeFlightCamera's logarithmic speed control.
      //
      // The exponential alone is fastest right at t=0, though, which reads
      // as snapping away from the departure point before the eye registers
      // it — the "doesn't feel like it started from here" complaint.
      // Applying it to an already-smoothstepped time axis keeps the
      // exponential's slow-arrival bias while giving both ends zero
      // velocity: `smoothstep`'s own derivative is 0 at s=0 and s=1, and
      // that 0 forces the composite's derivative to 0 there too regardless
      // of how steep the exponential is in between.
      const k = TOUR_TRANSIT_APPROACH_SHARPNESS;
      const s = smoothstep(0, 1, t);
      const eased = (1 - Math.exp(-k * s)) / (1 - Math.exp(-k));
      const position = this.transitFrom
        .clone()
        .addScaled(Vec3.subVectors(this.transitTo, this.transitFrom), eased);

      // Position is continuous by construction (it starts at `transitFrom`,
      // which is wherever the camera was), but orientation used to jump
      // straight to "look at the destination" on the very first frame —
      // fine coming out of a normal ascend (which already faces roughly that
      // way), jarring from a Prev/Next skip that can fire mid surface-pass,
      // still facing forward along the terrain. Ease from the captured
      // starting orientation instead, over the same short window the other
      // phase-boundary blends use.
      const naturalOrientation = this.lookAtCentre(position, basis, orbitRadius);
      const blendWindow = Math.min(TOUR_ORIENTATION_BLEND_SEC, duration * 0.5);
      const orientation =
        this.phaseElapsed < blendWindow
          ? Quat.slerp(this.transitStartOrientation, naturalOrientation, smootherstep(0, blendWindow, this.phaseElapsed))
          : naturalOrientation;

      host.freeFlight.position.copy(position);
      host.freeFlight.orientation.copy(orientation);
      if (t >= 1) {
        this.phase = 'orbit';
        this.phaseElapsed = 0;
      }
      return;
    }

    if (this.phase === 'orbit' || this.phase === 'descend') {
      this.spaceElapsed = Math.min(this.spaceElapsed + dt, this.spaceDuration);
      const u = this.spaceElapsed / this.spaceDuration;
      const position = splinePosition(this.spacePoints, u);
      let orientation = this.lookAtCentre(position, basis, orbitRadius);

      const orbitEnd = cfg.orbitDurationSec;
      const phase: TourPhase = !stop.hasSurface || this.spaceElapsed < orbitEnd ? 'orbit' : 'descend';
      this.phase = phase;
      this.phaseElapsed = phase === 'orbit' ? this.spaceElapsed : this.spaceElapsed - orbitEnd;

      // Ease the look-at-centre orientation toward the surface pass's own
      // forward-facing orientation over the descent's last stretch, so the
      // frame swap into the surface pass is a blend the eye has already
      // mostly made, not a snap.
      if (phase === 'descend') {
        const blendWindow = Math.min(TOUR_ORIENTATION_BLEND_SEC, cfg.descentDurationSec * 0.5);
        const blendStart = this.spaceDuration - blendWindow;
        if (this.spaceElapsed >= blendStart) {
          const w = smootherstep(blendStart, this.spaceDuration, this.spaceElapsed);
          const endPos = this.spacePoints[this.spacePoints.length - 1];
          const { dir, axis } = this.flybyStartFrom(endPos, body);
          const sweep = tourFlybySweepRad(cfg, body.radius + this.flybyAltitude);
          const previewFixed = surfaceFlybyPose(body.radius, this.flybyAltitude, dir, axis, sweep, 0).orientation;
          const previewInertial = frames.transformOrientation(
            previewFixed,
            body.fixedFrameId,
            body.inertialFrameId,
            host.time,
          );
          orientation = Quat.slerp(orientation, previewInertial, w);
        }
      }

      host.freeFlight.position.copy(position);
      host.freeFlight.orientation.copy(orientation);

      if (this.spaceElapsed >= this.spaceDuration) {
        if (stop.hasSurface) {
          const endPos = this.spacePoints[this.spacePoints.length - 1];
          const { dir, axis } = this.flybyStartFrom(endPos, body);
          this.flybyStartDir.copy(dir);
          this.flybyAxis.copy(axis);
          // Planned in `beginTransitTo`, which is what the descent just flew
          // down to. Re-scan the track as actually resolved and keep the
          // higher of the two: identical in the normal case (the planning scan
          // pads generously past both ends), and on the pathological one it
          // costs a small step up rather than a camera inside a ridge.
          this.flybyAltitude = Math.max(
            this.flybyAltitude,
            this.computeFlybyAltitude(body, cfg, dir, axis),
          );
          host.freeFlightFrame = 'fixed';
          this.phase = 'surface';
          this.phaseElapsed = 0;
        } else {
          this.advanceStop();
        }
      }
      return;
    }

    if (this.phase === 'surface') {
      const duration = Math.max(0.1, cfg.surfaceFlybyDurationSec);
      const t = clamp01(this.phaseElapsed / duration);
      const sweep = tourFlybySweepRad(cfg, body.radius + this.flybyAltitude);
      const pose = surfaceFlybyPose(body.radius, this.flybyAltitude, this.flybyStartDir, this.flybyAxis, sweep, t);

      // A slow side-to-side drift so the pass reads as a camera operator
      // scanning the ground, not a rigidly forward-locked shot.
      const panRad = (TOUR_FLYBY_PAN_DEG * Math.PI) / 180;
      const pan = Math.sin(t * TAU * TOUR_FLYBY_PAN_CYCLES) * panRad;
      let orientation = pose.orientation.multiply(Quat.fromAxisAngle(new Vec3(0, 1, 0), pan));

      // Mirror image of the descend-tail blend: ease toward the orientation
      // the climb-out will actually start with, over the pass's last stretch.
      const blendWindow = Math.min(TOUR_ORIENTATION_BLEND_SEC, duration * 0.5);
      const blendStart = duration - blendWindow;
      if (this.phaseElapsed >= blendStart) {
        const w = smootherstep(blendStart, duration, this.phaseElapsed);
        const finalFixed = surfaceFlybyPose(
          body.radius,
          this.flybyAltitude,
          this.flybyStartDir,
          this.flybyAxis,
          sweep,
          1,
        ).position;
        const finalInertial = frames.transformPosition(
          finalFixed,
          body.fixedFrameId,
          body.inertialFrameId,
          host.time,
        );
        const climbBasis = this.ascendBasisFrom(finalInertial, basis.normal);
        const targetInertial = orbitPose(finalInertial.length(), 0, climbBasis).orientation;
        const targetFixed = frames.transformOrientation(
          targetInertial,
          body.inertialFrameId,
          body.fixedFrameId,
          host.time,
        );
        orientation = Quat.slerp(orientation, targetFixed, w);
      }

      host.freeFlight.position.copy(pose.position);
      host.freeFlight.orientation.copy(orientation);

      if (t >= 1) {
        const endInertial = frames.transformPosition(
          pose.position,
          body.fixedFrameId,
          body.inertialFrameId,
          host.time,
        );
        this.ascendBasis = this.ascendBasisFrom(endInertial, basis.normal);
        this.ascendRadiusStart = endInertial.length();
        host.freeFlightFrame = 'inertial';
        this.phase = 'ascend';
        this.phaseElapsed = 0;
      }
      return;
    }

    // Ascend: a spiral climb in `ascendBasis`, whose `u` axis was built to
    // point exactly at the surface pass's ending position, so this starts
    // (angle 0) with no position jump from wherever the pass left off.
    const duration = Math.max(0.1, cfg.descentDurationSec);
    const t = clamp01(this.phaseElapsed / duration);
    const radiusEnd = orbitRadius;
    const pose = spiralPose(this.ascendRadiusStart, radiusEnd, 0, Math.PI * 0.5, t, t, this.ascendBasis!);
    host.freeFlight.position.copy(pose.position);
    host.freeFlight.orientation.copy(pose.orientation);
    if (t >= 1) this.advanceStop();
  }
}
